import "./tailwind.css";
import { SearchWrapper } from "./SearchWrapper";
import type { ReactElement, ReactNode } from "react";
import React, { useEffect, useState, useMemo, useRef, useCallback, Suspense, lazy } from "react";
import { IntlProvider } from 'react-intl';
import { defaultLocale } from '../i18n';
import { Messages } from '../types';
import { loadLocaleData } from '../utils/routing/url-helpers';
import { buildFooterStaticStrings } from '../utils/footer/buildFooterStaticStrings';
import { FooterStringsProvider } from '../contexts/FooterStringsContext';
import { usePageContext } from 'vike-react/usePageContext';
import SavedStateContext from '../contexts/SavedStateContext';
import { SearchProvider } from '../contexts/SearchContext';
import { getIndexName } from '../constants';
import { useSentryUser } from '../hooks/useSentryUser';
import { useProfilePreferencesSettingsSync } from '../hooks/useProfilePreferencesSettingsSync';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { NavigationProvider, useNavigation } from '../contexts/NavigationContext';
import { NavigationProgress } from '../components/NavigationProgress';
import { ToastProvider } from '../contexts/ToastContext';
import { ToastContainer } from '../components/Toast';
import { LocaleMismatchPrompt } from '../components/LocaleMismatchPrompt';
import { SRPShell } from '../pages/index/static/SRPShell';
import type { SessionUser } from '../types/auth';
import type { StaticSRPData } from '../types/srp-static';
import { SrpView } from '../features/srp/SrpView';
import type { SrpViewModel } from '../features/srp/SrpViewModel';
import { serializeForHtmlScript } from '../features/srp/serializeForHtmlScript';
import { parseSrpInitialScript } from '../features/srp/parseSrpInitialScript';
import { useSrpIsland } from '../contexts/SrpIslandContext';
import { AuthProvider } from '../contexts/AuthContext';
import { NavigationNavbar } from '../components/navigation/NavigationNavbar';
import { navigateToLocaleRoot } from '../utils/routing/navigateToLocaleRoot';
import { useIntl } from 'react-intl';

const MobileMenuModal = lazy(() =>
  import('../components/navigation/MobileMenuModal').then((m) => ({ default: m.MobileMenuModal }))
);

const SUPPORTED_LOCALES = ['en', 'fi', 'sv'] as const;

/** Path segments (without locale) that use the minimal layout (nav + content, no SearchProvider/Redux). */
const MINIMAL_ROUTE_SEGMENTS = new Set([
  'resources', 'tyoelama', 'arbetsliv',
  'business', 'liiketoiminta', 'foretag',
  'for-employers', 'tyonantajille', 'for-arbetsgivare',
  'locations', 'sijainnit', 'platser',
]);

/** Sub-routes that need full layout (SearchWrapper) even when under resources. */
const FULL_LAYOUT_SUBROUTES = new Set([
  'summer-jobs', 'kesatyot', 'sommarjobb',
  'students-and-graduates', 'opiskelijat-ja-valmistuneet', 'studenter-och-nyexaminerade',
]);

function isMinimalRoute(path: string): boolean {
  const segments = String(path).split('/').filter(Boolean);
  if (segments.some((s) => FULL_LAYOUT_SUBROUTES.has(s.toLowerCase()))) return false;
  const first = segments[0]?.toLowerCase();
  const second = segments[1]?.toLowerCase();
  if (first && MINIMAL_ROUTE_SEGMENTS.has(first)) return true;
  if (first && ['en', 'fi', 'sv'].includes(first) && second && MINIMAL_ROUTE_SEGMENTS.has(second)) return true;
  return false;
}

/** Lightweight chrome for non-actionable pages: nav bar + mobile menu, no SearchProvider/Redux. */
function MinimalChrome({ children }: { children: ReactNode }) {
  const intl = useIntl();
  const pageContext = usePageContext();
  const pathname =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  const urlOriginal =
    (pageContext as any)?.urlOriginal ??
    (typeof window !== 'undefined' ? window.location.href : pathname);
  const fullUrlContent = urlOriginal.startsWith('http') ? urlOriginal : pathname;
  const user = (pageContext?.user ?? null) as SessionUser | null;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const clearSearch = useCallback(async () => {
    await navigateToLocaleRoot(intl.locale);
  }, [intl.locale]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      <header role="banner">
        <NavigationNavbar
          intl={intl}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          clearSearch={clearSearch}
          user={user}
        />
      </header>
      <div className="flex-1 flex lg:flex-row lg:relative overflow-hidden min-h-0">
        <Suspense fallback={null}>
          <MobileMenuModal
            isOpen={isMobileMenuOpen}
            onRequestClose={() => setIsMobileMenuOpen(false)}
            onCloseComplete={() => setIsMobileMenuOpen(false)}
            fullUrlContent={fullUrlContent}
            intl={intl}
          />
        </Suspense>
        <main
          className={`flex-1 overflow-x-hidden overflow-y-auto min-h-0 pt-1 sm:pt-1 md:pt-2 pb-20 lg:pb-0 sm:px-0 md:px-2 lg:px-8 transition-transform duration-300 ease-out ${
            isMobileMenuOpen ? 'lg:ml-[420px]' : 'lg:ml-0'
          }`}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

/** Get locale from pathname (first segment if en/fi/sv). Client-side fallback when pageContext.locale isn't ready during hydration. */
function getLocaleFromPathname(pathname: string): (typeof SUPPORTED_LOCALES)[number] | null {
  const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return first && (SUPPORTED_LOCALES as readonly string[]).includes(first) ? (first as (typeof SUPPORTED_LOCALES)[number]) : null;
}

// Utility to determine if this is the search page (homepage for the locale)
function isSearchPageVike(pageContext: { urlLogical?: string; urlPathname?: string } | null | undefined) {
  // Prefer urlPathname when available (vike provides it), else urlLogical. On client during hydration,
  // pageContext may not be ready yet—fall back to window.location.pathname to avoid showing SRP navbar on resource pages.
  const path =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  const segments = String(path).split('/').filter(Boolean);
  
  // Search page when:
  // - root "/"
  // - locale-only root "/en", "/fi", "/sv"
  // - locale + jobs route (and any subpath) e.g. "/en/jobs/...", "/fi/tyopaikat/...", "/sv/lediga-jobb/..."
  // - jobs route without locale "/jobs", "/tyopaikat", "/jobb" (when locale is stripped)
  if (segments.length === 0) return true;
  
  const [first, second] = [segments[0], segments[1]];
  const isLocale = ["en", "fi", "sv"].includes(first);
  
  // Handle locale-only paths
  if (isLocale && segments.length === 1) return true;
  
  // Handle locale + jobs paths
  const jobsSegments = { en: 'jobs', fi: 'tyopaikat', sv: 'jobb' } as const;
  if (isLocale) {
    const expected = (jobsSegments as any)[first] as string;
    if (second === expected) return true;
  }
  
  // Handle jobs paths without locale (when locale is stripped by Vike)
  const allJobSegments = ['jobs', 'tyopaikat', 'jobb'];
  if (segments.length >= 1 && allJobSegments.includes(first)) return true;
  
  return false;
}

// Wrapper to memoize the arrays before passing to the provider
// Prevents array recreation on every render
function SavedStateContextProviderMemoizedMemo({
  children,
  savedJobIds,
  appliedJobIds,
  viewedJobIds,
}: {
  children: ReactNode;
  savedJobIds?: string[];
  appliedJobIds?: string[];
  viewedJobIds?: string[];
}) {
  // Memoize arrays to prevent recreation if pageContext.data changes by reference
  const memoizedSavedJobIds = useMemo(() => savedJobIds || [], [savedJobIds?.join(',')]);
  const memoizedAppliedJobIds = useMemo(() => appliedJobIds || [], [appliedJobIds?.join(',')]);
  const memoizedViewedJobIds = useMemo(() => viewedJobIds || [], [viewedJobIds?.join(',')]);
  
  return (
    <SavedStateContextProviderMemoized
      savedJobIds={memoizedSavedJobIds}
      appliedJobIds={memoizedAppliedJobIds}
      viewedJobIds={memoizedViewedJobIds}
    >
      {children}
    </SavedStateContextProviderMemoized>
  );
}

/** Wrapper that calls useProfilePreferencesSettingsSync. Renders null. Used so we can catch Redux Provider missing via ErrorBoundary. */
function ProfilePreferencesSettingsSyncGuard({ userId }: { userId: string | null | undefined }) {
  useProfilePreferencesSettingsSync(userId ?? null);
  return null;
}

// Memoized SavedStateContext provider to prevent unnecessary re-renders
// Only recreates Sets when the actual IDs change
function SavedStateContextProviderMemoized({ 
  children, 
  savedJobIds, 
  appliedJobIds, 
  viewedJobIds 
}: { 
  children: ReactNode;
  savedJobIds: string[];
  appliedJobIds: string[];
  viewedJobIds: string[];
}) {
  // Create stable string keys for dependency tracking (sorted for consistency, order shouldn't matter for Sets)
  const savedKey = JSON.stringify([...savedJobIds].sort());
  const appliedKey = JSON.stringify([...appliedJobIds].sort());
  const viewedKey = JSON.stringify([...viewedJobIds].sort());
  
  const savedStateValue = useMemo(() => ({
    saved: new Set<string>(savedJobIds),
    applied: new Set<string>(appliedJobIds),
    viewed: new Set<string>(viewedJobIds),
  }), [savedKey, appliedKey, viewedKey]);
  
  return (
    <SavedStateContext.Provider value={savedStateValue}>
      {children}
    </SavedStateContext.Provider>
  );
}

function LayoutContent({ children }: { children: ReactNode }) {
  const pageContext = usePageContext();
  const pathnameForLocale = (pageContext as any)?.urlPathname ?? (pageContext as any)?.urlLogical ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const locale = (pageContext?.locale as string) || getLocaleFromPathname(pathnameForLocale) || defaultLocale;
  // Memoize messages and footer strings (SSR-computed; footer never uses react-intl)
  const messages: Messages = useMemo(() => loadLocaleData(locale), [locale]);
  const footerStrings = useMemo(() => buildFooterStaticStrings(messages), [messages]);
  
  // Try to get navigation context to complete navigation when page changes
  let navigation: { completeNavigation: () => void } | null = null;
  try {
    navigation = useNavigation();
  } catch {
    // Navigation context not available
  }
  
  // Setup Sentry user context
  useSentryUser();
  
  // Track initial load to prevent scroll/navigation on SSR hydration
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [previousPathname, setPreviousPathname] = useState<string | undefined>(pageContext?.urlPathname);

  // Mark initial load as complete after hydration
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsInitialLoad(false);
      setPreviousPathname(pageContext?.urlPathname);
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  // Complete navigation when page context changes (actual navigation completed)
  // Also scroll to top on page change (but not on initial SSR load)
  useEffect(() => {
    // Skip on initial load - SSR already has the page at the right scroll position
    if (isInitialLoad) return;
    
    // Only scroll if the pathname actually changed (real navigation)
    if (previousPathname === pageContext?.urlPathname) return;

    if (typeof window !== 'undefined') {
      // Use 'auto' instead of 'instant' to prevent scrollbar flash
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    if (navigation) {
      // Small delay to ensure page has rendered
      const timeout = setTimeout(() => {
        navigation.completeNavigation();
      }, 100);
      return () => clearTimeout(timeout);
    }
    
    setPreviousPathname(pageContext?.urlPathname);
  }, [pageContext?.urlPathname, navigation, isInitialLoad, previousPathname]);
  
  // Compute isSearchPage on every render to ensure it updates with client-side navigation
  const isSearchPage = isSearchPageVike(pageContext as any);
  const isSrpIsland = useSrpIsland();
  
  // Get server state from page data if available
  // Memoize to prevent creating new object references on every render
  const serverState = useMemo(() => (pageContext?.data as any)?.serverState, [pageContext?.data]);
  const searchData = useMemo(() => {
    const data = (pageContext?.data as any) || {};
    // Only warn about missing search data on search pages (where it's actually needed)
    if (typeof window !== 'undefined' && isSearchPage && !data.initialQuery && !data.initialResults) {
      console.warn('⚠️ [LayoutContent] pageContext.data missing initialQuery/initialResults', {
        hasData: !!pageContext?.data,
        dataKeys: pageContext?.data ? Object.keys(pageContext?.data) : [],
        urlPathname: pageContext?.urlPathname,
      });
    }
    return data;
  }, [pageContext?.data, pageContext?.urlPathname, isSearchPage]);
  
  // Create stable keys for dependency tracking
  const initialResults = searchData.initialResults || null;
  const initialResultsKey = initialResults 
    ? `${initialResults.nbHits}-${initialResults.page}-${initialResults.hits?.length || 0}`
    : 'null';
  const initialFiltersKey = JSON.stringify(searchData.initialFilters || {});
  const facetsDataKey = searchData.facetsData ? JSON.stringify(Object.keys(searchData.facetsData).sort()) : 'null';
  
  // Memoize SearchProvider props to prevent re-renders when searchData object reference changes
  const searchProviderProps = useMemo(() => ({
    indexName: searchData.indexName || getIndexName(locale),
    initialQuery: searchData.initialQuery || '',
    initialFilters: searchData.initialFilters || {},
    initialPage: searchData.initialPage || 0,
    initialResults,
    initialSort: searchData.initialSort || 'relevance',
  }), [
    searchData.indexName,
    searchData.initialQuery,
    searchData.initialPage,
    initialResultsKey, // Compare by content, not reference
    initialFiltersKey, // Compare by content, not reference
    searchData.initialSort,
    locale,
  ]);
  
  // Memoize SearchWrapper props (Step 3: use lite shell on SRP to defer filter bar / mobile nav)
  const searchWrapperProps = useMemo(() => ({
    isSearchPage,
    useLiteShell: isSearchPage,
    serverState,
    ssrIsMobile: (pageContext?.data as any)?.ssrIsMobile,
    ssrIsXl: (pageContext?.data as any)?.ssrIsXl,
    initialFacetsData: searchData.facetsData,
  }), [
    isSearchPage,
    serverState,
    (pageContext?.data as any)?.ssrIsMobile,
    (pageContext?.data as any)?.ssrIsXl,
    facetsDataKey,
  ]);

  // Static SRP data for Option B shell (no providers on initial paint)
  const staticSRPData = useMemo((): StaticSRPData => ({
    initialResults: searchData.initialResults ?? null,
    initialQuery: searchData.initialQuery ?? '',
    initialFilters: searchData.initialFilters ?? {},
    initialPage: searchData.initialPage ?? 0,
    indexName: searchData.indexName || getIndexName(locale),
    initialSavedJobIds: searchData.initialSavedJobIds,
    initialAppliedJobIds: searchData.initialAppliedJobIds,
    initialViewedJobIds: searchData.initialViewedJobIds,
    initialSort: searchData.initialSort,
    initialLayout: searchData.initialLayout,
    facetsData: searchData.facetsData,
    hasNoResults: (pageContext?.data as any)?.hasNoResults,
    locationTrendsData: (pageContext?.data as any)?.locationTrendsData,
  }), [
    searchData.initialResults,
    searchData.initialQuery,
    searchData.initialFilters,
    searchData.initialPage,
    searchData.indexName,
    searchData.initialSavedJobIds,
    searchData.initialAppliedJobIds,
    searchData.initialViewedJobIds,
    searchData.initialSort,
    searchData.initialLayout,
    searchData.facetsData,
    (pageContext?.data as any)?.hasNoResults,
    (pageContext?.data as any)?.locationTrendsData,
    locale,
  ]);

  // Use window.location.pathname as fallback on client during hydration so we don't incorrectly
  // get pathname='/' when pageContext isn't ready yet (which would make isSearchPage true and show SRP navbar).
  const pathname =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');

  // On client hydration, pageContext.data.srpViewModel may be missing. Read #srp-initial from the DOM
  // synchronously and cache in a ref so we take the same static branch as the server (avoid hydration mismatch).
  // useRef + read in body ensures we see the server-rendered script on the first client render.
  const srpViewModelFromScriptRef = useRef<SrpViewModel | null | undefined>(undefined);
  if (typeof window !== 'undefined' && srpViewModelFromScriptRef.current === undefined) {
    const el = document.getElementById('srp-initial');
    if (el && el.tagName === 'SCRIPT') {
      srpViewModelFromScriptRef.current = parseSrpInitialScript((el as HTMLScriptElement).textContent ?? '');
    } else {
      srpViewModelFromScriptRef.current = null;
    }
  }
  const fromWindow =
    typeof window !== 'undefined' ? (window as any).__SRP_VIEW_MODEL__ as SrpViewModel | null | undefined : null;
  const clientSrpViewModel =
    typeof window !== 'undefined' ? srpViewModelFromScriptRef.current ?? fromWindow ?? null : null;

  const srpViewModel = (searchData as any).srpViewModel ?? clientSrpViewModel;
  // Only use for static render when we have a full view model (not the _fetch bootstrap).
  const fullSrpViewModel: SrpViewModel | null =
    srpViewModel &&
    typeof srpViewModel === 'object' &&
    !('_fetch' in srpViewModel && (srpViewModel as any)._fetch === true) &&
    typeof (srpViewModel as SrpViewModel).locale === 'string' &&
    Array.isArray((srpViewModel as SrpViewModel).jobs)
      ? (srpViewModel as SrpViewModel)
      : null;

  if (typeof window !== 'undefined' && (window as any).__SRP_DEBUG_LAYOUT__) {
    console.log('[LayoutContent] branch', {
      isSearchPage,
      isSrpIsland,
      hasSrpViewModel: !!srpViewModel,
      hasFullSrpViewModel: !!fullSrpViewModel,
    });
  }

  // Progressive SRP takeover (no hydration): server-only static HTML + empty container + snapshot.
  // Client mounts into #srp-app (overlaid on #srp-ssr so only one contributes to layout, avoiding CLS/scrollbar),
  // then removes #srp-ssr and shows #srp-app (see srp.client.ts).
  // We need fullSrpViewModel for the static shell; when script only has _fetch, island fetches from /api/srp-state.
  if (isSearchPage && fullSrpViewModel && !isSrpIsland) {
    return (
      <>
        <div id="srp-wrapper" style={{ position: 'relative', minHeight: '100vh' }}>
          <div id="srp-ssr">
            <SrpView model={fullSrpViewModel} mode="static" />
          </div>
          <div
            id="srp-app"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              visibility: 'hidden',
            }}
            aria-hidden="true"
          />
        </div>
        <script
          id="srp-initial"
          type="application/json"
          dangerouslySetInnerHTML={{
            __html: serializeForHtmlScript({ _fetch: true } as any),
          }}
        />
      </>
    );
  }

  return (
    <>
      <ErrorBoundary fallback={null}>
        <ProfilePreferencesSettingsSyncGuard userId={pageContext?.user?.id ?? null} />
      </ErrorBoundary>
      <IntlProvider 
        messages={messages} 
        locale={locale} 
        defaultLocale="en"
        onError={(err) => {
          // Suppress MISSING_TRANSLATIONS errors
          if (err.code === 'MISSING_TRANSLATION') {
            return;
          }
          // Log other errors
          console.error('[react-intl] error:', err);
        }}
      >
        <FooterStringsProvider strings={footerStrings}>
        <LocaleMismatchPrompt />
        {isSearchPage ? (
        <SRPShell
          data={staticSRPData}
          locale={locale}
          pathname={pathname}
          interactiveTree={
            <SavedStateContextProviderMemoizedMemo
              savedJobIds={searchData.initialSavedJobIds}
              appliedJobIds={searchData.initialAppliedJobIds}
              viewedJobIds={searchData.initialViewedJobIds}
            >
              <SearchProvider
                indexName={searchProviderProps.indexName}
                initialQuery={searchProviderProps.initialQuery}
                initialFilters={searchProviderProps.initialFilters}
                initialPage={searchProviderProps.initialPage}
                initialResults={searchProviderProps.initialResults}
                initialSort={searchProviderProps.initialSort}
                initialViewMode={(searchData.initialLayout as 'grid' | 'list' | 'map') || 'list'}
                syncToUrl={true}
              >
                <SearchWrapper {...searchWrapperProps}>
                  {children}
                </SearchWrapper>
              </SearchProvider>
            </SavedStateContextProviderMemoizedMemo>
          }
        />
      ) : isMinimalRoute(pathname) ? (
        <MinimalChrome>{children}</MinimalChrome>
      ) : (
        <SearchProvider
          indexName={getIndexName(locale)}
          initialQuery={''}
          initialFilters={{}}
          initialPage={0}
          initialResults={null}
          initialSort={'relevance'}
          syncToUrl={false}
        >
          <SearchWrapper 
            isSearchPage={isSearchPage} 
            serverState={serverState}
            initialFacetsData={searchData.facetsData}
          >
            {children}
          </SearchWrapper>
        </SearchProvider>
      )}
        </FooterStringsProvider>
      </IntlProvider>
    </>
  );
}

/** Wraps layout in IntlProvider so Page (and any child) always has intl during hydration. */
function LayoutDefaultWithIntl({ children }: { children: ReactNode }) {
  const pageContext = usePageContext();
  const pathnameForLocale = (pageContext as any)?.urlPathname ?? (pageContext as any)?.urlLogical ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const locale = (pageContext?.locale as string) || getLocaleFromPathname(pathnameForLocale) || defaultLocale;
  const messages: Messages = useMemo(() => loadLocaleData(locale), [locale]);
  const initialUser = (pageContext?.user ?? null) as SessionUser | null;
  return (
    <IntlProvider
      messages={messages}
      locale={locale}
      defaultLocale="en"
      onError={(err) => {
        if (err.code === 'MISSING_TRANSLATION') return;
        console.error('[react-intl] error:', err);
      }}
    >
      <AuthProvider initialUser={initialUser}>
        <NavigationProvider>
          <ToastProvider>
            <NavigationProgress />
            <ToastContainer />
            <LayoutContent>{children}</LayoutContent>
          </ToastProvider>
        </NavigationProvider>
      </AuthProvider>
    </IntlProvider>
  );
}

export default function LayoutDefault({ children }: { children: ReactNode }) {
  return <LayoutDefaultWithIntl>{children}</LayoutDefaultWithIntl>;
}
